import { useState, useRef, useEffect } from "react";
import { Paperclip, Send, Globe, X, FileText, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";

// Language list matching backend - NO Punjabi
const LANGUAGES = [
  { code: "hi", name: "Hindi" },
  { code: "bn", name: "Bengali" },
  { code: "gu", name: "Gujarati" },
  { code: "kn", name: "Kannada" },
  { code: "ml", name: "Malayalam" },
  { code: "mr", name: "Marathi" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "or", name: "Odia" },
  { code: "as", name: "Assamese" },
  { code: "mni", name: "Manipuri" },
  { code: "en-IN", name: "Indian English" },
];

// Class groups
const CLASS_GROUPS = [
  { id: "1-2", label: "Class 1–2" },
  { id: "3-4", label: "Class 3–4" },
  { id: "5-6", label: "Class 5–6" },
  { id: "7-8", label: "Class 7–8" },
];

// Topics per class (with emoji)
const CLASS_TOPICS = {
  "1-2": ["Plants 🌱", "Animals 🐘", "Shapes 🔺", "My Body 👁️", "Weather ☁️", "Birds 🦜", "Numbers 1-100 🔢", "Fruits & Vegetables 🥭"],
  "3-4": ["Water Cycle 💧", "Fractions 🍕", "Solar System 🌍", "Food Chain 🦁", "Maps & Directions 🗺️", "Digestion 🫁", "Magnets 🧲", "Folk Tales 📖"],
  "5-6": ["Photosynthesis 🌿", "Gravity ⬇️", "Indian Constitution 📜", "Decimals 🔢", "Light & Shadow 💡", "Reproduction in Plants 🌸", "Climate Change 🌡️", "Mughal Empire 🏰"],
  "7-8": ["Newton's Laws ⚡", "Cell Structure 🔬", "Algebra 📐", "French Revolution 🏛️", "Acids & Bases 🧪", "Human Respiratory System 🫁", "Rational Numbers 📊", "Nationalism in India 🇮🇳"],
};

// Cycles through 4 colors for topic pills
const PILL_COLORS = [
  { bg: "bg-[#448AFF]/10", border: "border-[#448AFF]/50", text: "text-[#448AFF]" },
  { bg: "bg-[#FFD740]/10", border: "border-[#FFD740]/50", text: "text-[#FFD740]" },
  { bg: "bg-[#00D4AA]/10", border: "border-[#00D4AA]/50", text: "text-[#00D4AA]" },
  { bg: "bg-[#FF6B6B]/10", border: "border-[#FF6B6B]/50", text: "text-[#FF6B6B]" },
];

export const UnifiedInput = ({
  onGenerate,
  disabled,
  pendingPrompt,
  setPendingPrompt,
  forceMode, // "snap" | "type" | null
}) => {
  const [inputText, setInputText] = useState(pendingPrompt || "");
  const [language, setLanguage] = useState("hi");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedClass, setSelectedClass] = useState("3-4");
  const fileInputRef = useRef(null);

  // Sync pending prompt to input when it changes
  useEffect(() => {
    if (pendingPrompt) setInputText(pendingPrompt);
  }, [pendingPrompt]);

  // When forceMode = "snap", auto-trigger file picker
  useEffect(() => {
    if (forceMode === "snap" && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [forceMode]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PDF or image file (PNG, JPG, WEBP)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setUploadedFile(file);
      setInputText("");
      // In snap mode, auto-submit as soon as file is selected
      if (forceMode === 'snap') {
        const content = { type: file.type.includes('pdf') ? 'pdf' : 'image', file };
        onGenerate(content, language);
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = () => {
    if (!inputText.trim() && !uploadedFile) return;

    const content = uploadedFile
      ? { type: uploadedFile.type.includes('pdf') ? 'pdf' : 'image', file: uploadedFile }
      : { type: 'text', text: inputText.trim() };

    onGenerate(content, language);
    if (setPendingPrompt) setPendingPrompt("");
  };

  const handleTopicClick = (topic) => {
    // Strip trailing emoji characters before filling input
    const cleanTopic = topic.replace(/[\s]*[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]+\s*$/gu, "").trim();
    setInputText(cleanTopic || topic.trim());
    setUploadedFile(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getFileIcon = () => {
    if (!uploadedFile) return null;
    return uploadedFile.type.includes('pdf') ? FileText : ImageIcon;
  };

  const FileIcon = getFileIcon();

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4 md:space-y-6" data-testid="unified-input">
      {/* Main Input Bar */}
      <div className="relative">
        <div className="flex items-center gap-1 md:gap-2 p-1.5 md:p-2 rounded-xl md:rounded-2xl bg-white dark:bg-[#141E32] border dark:border-[#2A2A3E] hover:border-[#448AFF]/30 dark:hover:border-[#448AFF]/50 transition-colors">

          {/* Left Side: Upload & Language */}
          <div className="flex items-center gap-0.5 md:gap-1 pl-1 md:pl-2">
            {/* File Upload Button — always present (needed for snap) */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,image/*"
              className="hidden"
              disabled={disabled}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="h-8 w-8 md:h-10 md:w-10 rounded-lg md:rounded-xl hover:bg-[#448AFF]/10 dark:hover:bg-[#448AFF]/10"
              data-testid="upload-btn"
              aria-label="Upload file"
            >
              <Paperclip className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
            </Button>

            {/* Language Selector — hidden in snap mode */}
            {forceMode !== 'snap' && (
              <>
                <Select value={language} onValueChange={setLanguage} disabled={disabled}>
                  <SelectTrigger
                    className="w-auto min-w-[80px] md:min-w-[100px] border-0 bg-transparent shadow-none focus:ring-0 h-8 md:h-10 text-xs md:text-sm"
                    data-testid="language-select-trigger"
                  >
                    <Globe className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent data-testid="language-dropdown">
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="w-px h-5 md:h-6 bg-border" />
              </>
            )}
          </div>

          {/* Center: Input Field or File Preview */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              {uploadedFile ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#448AFF]/10 dark:bg-[#448AFF]/15"
                >
                  {FileIcon && <FileIcon className="h-4 w-4 text-[#448AFF]" />}
                  <span className="text-sm truncate max-w-[200px] text-[#448AFF]">
                    {uploadedFile.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeFile}
                    className="h-6 w-6 rounded-full hover:bg-[#448AFF]/20"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </motion.div>
              ) : forceMode !== 'snap' ? (
                <motion.input
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="What do you want to learn today?"
                  disabled={disabled}
                  className="w-full bg-transparent border-0 outline-none text-sm md:text-base px-2 py-2 placeholder:text-muted-foreground/60"
                  data-testid="main-input"
                  aria-label="Enter a topic to learn about"
                />
              ) : (
                <span className="text-sm text-muted-foreground px-2">Select a file to snap…</span>
              )}
            </AnimatePresence>
          </div>

          {/* Right Side: Generate Button — hidden in snap mode (file auto-submits) */}
          {forceMode !== 'snap' && (
            <Button
              onClick={handleSubmit}
              disabled={disabled || (!inputText.trim() && !uploadedFile)}
              className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-[#448AFF] hover:bg-[#3370CC] text-white disabled:opacity-40 shrink-0 transition-colors"
              data-testid="generate-btn"
              aria-label="Generate story"
            >
              <Send className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </Button>
          )}
        </div>
      </div>

      {/* Class Selector + Topic Pills — hidden in snap mode */}
      {forceMode !== 'snap' && <div className="text-center" data-testid="class-topic-section">
        <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-3">
          Pick your class, then tap a topic:
        </p>
        {/* Class selector row */}
        <div className="flex justify-center gap-1.5 mb-3 flex-wrap">
          {CLASS_GROUPS.map((cls) => (
            <button
              key={cls.id}
              onClick={() => setSelectedClass(cls.id)}
              disabled={disabled}
              className={`px-3 py-1 text-xs font-heading font-semibold rounded-full border transition-all disabled:opacity-50 ${
                selectedClass === cls.id
                  ? "bg-[#448AFF] border-[#448AFF] text-white"
                  : "bg-transparent border-[#448AFF]/40 text-[#448AFF] hover:bg-[#448AFF]/10"
              }`}
            >
              {cls.label}
            </button>
          ))}
        </div>
        {/* Topic pills */}
        <div className="flex flex-wrap justify-center gap-1.5 md:gap-2">
          {CLASS_TOPICS[selectedClass].map((topic, i) => {
            const color = PILL_COLORS[i % PILL_COLORS.length];
            return (
              <button
                key={topic}
                onClick={() => handleTopicClick(topic)}
                disabled={disabled}
                className={`px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm rounded-full border ${color.bg} ${color.border} ${color.text} hover:opacity-80 transition-all disabled:opacity-50`}
                data-testid={`topic-${topic.toLowerCase().replace(/[\s\W]+/g, '-').replace(/-+$/, '')}`}
              >
                {topic}
              </button>
            );
          })}
        </div>
      </div>}
    </div>
  );
};
